import React, { useContext, useRef, useEffect, useCallback } from 'react';
import { Box, TextInput, ActionIcon, Tooltip } from "@mantine/core";
import { IconSearch, IconX } from "@tabler/icons";
import IdeasContext from '../IdeasContext';
import { useClickOutside } from "@mantine/hooks";

interface FilterPanelProps {
  toggle: () => void;
  search: string;
  setSearch: (search: string) => void;
  searchOpened: boolean;
  setSearchOpened: (opened: boolean) => void;
}

const EXPANDED_WIDTH = 280;

const SearchPanel: React.FC<FilterPanelProps> = ({ search, setSearch, searchOpened, setSearchOpened }) => {
  const { setSortStatus } = useContext(IdeasContext);
  const inputRef = useRef<HTMLInputElement>(null);

  const isExpanded = searchOpened || !!search;

  const handleClose = useCallback(() => {
    if (!search) {
      setSearchOpened(false);
    }
  }, [search, setSearchOpened]);

  const clickOutsideRef = useClickOutside(handleClose);

  useEffect(() => {
    if (searchOpened && inputRef.current) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [searchOpened]);

  const handleClear = () => {
    setSearch('');
    setSortStatus({ columnAccessor: "none", direction: "asc" });
    setSearchOpened(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClear();
      inputRef.current?.blur();
    }
  };

  return (
    <Box ref={clickOutsideRef} sx={{ position: 'relative', width: 32, height: 32 }}>
      {/* Collapsed icon — always in flow */}
      {!isExpanded && (
        <Tooltip withinPortal withArrow label="Search inside the Ideas table">
          <ActionIcon
            variant="outline"
            size={32}
            onClick={() => setSearchOpened(true)}
            sx={{
              borderColor: "#D8D8DB",
              color: "#000",
              ":hover": {
                borderColor: "#5B60E3",
                color: "#5B60E3",
              },
            }}
          >
            <IconSearch size={16} />
          </ActionIcon>
        </Tooltip>
      )}

      {/* Expanded input — absolutely positioned, extends leftward */}
      <Box
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          width: EXPANDED_WIDTH,
          opacity: isExpanded ? 1 : 0,
          transform: isExpanded ? 'scaleX(1)' : 'scaleX(0)',
          transformOrigin: 'right center',
          transition: 'opacity 0.2s ease, transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          pointerEvents: isExpanded ? 'auto' : 'none',
        }}
      >
        <TextInput
          ref={inputRef}
          placeholder="Search ideas..."
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          onKeyDown={handleKeyDown}
          icon={<IconSearch size={16} />}
          size="xs"
          sx={{
            ".mantine-TextInput-icon": {
              color: "#000000",
            },
            input: {
              height: 32,
              fontSize: 12,
              borderColor: "#D8D8DB",
              ":hover": {
                borderColor: "#5B60E3"
              },
            },
          }}
          rightSection={
            search ? (
              <ActionIcon size="sm" onClick={handleClear}>
                <IconX color="#000000" size={14} />
              </ActionIcon>
            ) : null
          }
        />
      </Box>
    </Box>
  );
}

export default SearchPanel;
